import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch } from "@/redux/hooks";
import { setCurrentLeadIndex } from "@/pages/Private/redux/list/list.slice";

import { ListPerson } from "../../../List/schema/list";

interface UseComposeAddPersonArgs {
	inbox: ListPerson[];
	isFetchingInbox: boolean;
	// Open the chat tab for the just-added lead.
	onOpenChat: () => void;
	// Relax the inbox Score filter so the freshly-added (unscored) lead is shown + persists.
	onShowAllPriority: () => void;
}

/**
 * Wires the Compose page to GlobalSearchModal's single-select "Write message" flow. Returns the
 * `onPersonAdded` callback to pass to the modal: it opens the chat, relaxes the Score filter, and
 * arms a by-id selection so — once the resulting (filter-reset) refetch settles — the Compose inbox
 * selects that exact lead rather than relying on index 0. Kept out of Compose.tsx as a cohesive
 * unit (the page is already very large).
 */
export function useComposeAddPerson({
	inbox,
	isFetchingInbox,
	onOpenChat,
	onShowAllPriority,
}: UseComposeAddPersonArgs): (newLeadId?: number) => void {
	const dispatch = useAppDispatch();
	// Holds the pending lead id to select, plus whether the post-add refetch has begun.
	const composeSelectRef = useRef<{ id: number; refetchStarted: boolean } | null>(null);

	// Once the post-add (filter-reset) refetch settles, select the freshly composed lead by id.
	useEffect(() => {
		const target = composeSelectRef.current;

		if (!target) {
			return;
		}

		// Wait for the refetch to actually begin before matching, so the optimistic pre-refetch
		// inbox (the add's matcher prepend) doesn't select prematurely.
		if (isFetchingInbox) {
			target.refetchStarted = true;

			return;
		}

		if (!target.refetchStarted) {
			return;
		}

		const index = inbox.findIndex((person) => person.id === target.id);

		if (index >= 0) {
			dispatch(setCurrentLeadIndex(index));
			composeSelectRef.current = null;
		}
	}, [inbox, isFetchingInbox, dispatch]);

	return useCallback(
		(newLeadId?: number) => {
			onOpenChat();

			if (newLeadId != null) {
				composeSelectRef.current = { id: newLeadId, refetchStarted: false };
			}

			onShowAllPriority();
		},
		[onOpenChat, onShowAllPriority]
	);
}
